import { useCallback, useEffect, useRef } from 'react';
import { type Blocker, useBlocker } from 'react-router';

const BYPASS_WINDOW_MS = 1_000;

/**
 * Asks before throwing away unsaved changes (PUSH-05 AC 7): SPA navigations
 * (links, Cancelar, browser back/gesture) are held by `useBlocker` so the
 * caller can render `DiscardChangesDialog`; reloads and tab closes get the
 * browser's own `beforeunload` prompt. No native `confirm()` (unlike
 * `useBlockerFull`, which stays as is for the import).
 *
 * `bypass(fn)` runs `fn` with the guard off — used for the test deep link
 * (design R11) and for navigations after a successful create. The guard comes
 * back after `BYPASS_WINDOW_MS`, so a `beforeunload` the browser dispatches
 * asynchronously for an external-protocol link is not caught either.
 */
export function useDiscardGuard(isDirty: boolean): {
  blocker: Blocker;
  proceed(): void;
  reset(): void;
  bypass<T>(fn: () => T): T;
} {
  const bypassing = useRef(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !bypassing.current &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search)
  );

  useEffect(() => {
    if (!isDirty) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (bypassing.current) return;
      // The browser shows its own generic prompt; custom text is ignored.
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const proceed = useCallback(() => blocker.proceed?.(), [blocker]);
  const reset = useCallback(() => blocker.reset?.(), [blocker]);

  const bypass = useCallback(<T>(fn: () => T): T => {
    bypassing.current = true;
    try {
      return fn();
    } finally {
      setTimeout(() => {
        bypassing.current = false;
      }, BYPASS_WINDOW_MS);
    }
  }, []);

  return { blocker, proceed, reset, bypass };
}
